import { Asset } from '../../services/assetService';

export type DeploymentTarget =
    | { kind: 'person'; name: string; detail?: string }
    | { kind: 'location'; name: string; detail?: string }
    | { kind: 'none' };

/**
 * What an asset is currently deployed *to*.
 *
 * `AssetsService.deploy()` clears the assignee for a LOCATION target — a switch
 * in a rack has no custodian — so the categories that only allow LOCATION
 * deployment (Network, Printer, Server) always have a null assignee. Screens
 * that read the assignee alone therefore showed a deployed asset as
 * "Unassigned" with its location nowhere in sight (BUG-003).
 *
 * Callers get one answer instead of each re-deriving it and drifting apart.
 */
export function deploymentTarget(asset: Asset): DeploymentTarget {
    if (asset.assignedTo) {
        return {
            kind: 'person',
            name: `${asset.assignedTo.firstName} ${asset.assignedTo.lastName}`,
            detail: asset.assignedTo.email,
        };
    }

    // Site/building/floor/room are captured alongside the location on deploy;
    // they qualify it rather than replace it, so they render as the detail line.
    const place = asset.location || asset.site;
    if (place) {
        const qualifiers = [asset.site, asset.building, asset.floor, asset.roomDesk]
            .filter((part) => part && part !== place);
        return {
            kind: 'location',
            name: place,
            detail: qualifiers.length ? qualifiers.join(' • ') : undefined,
        };
    }

    return { kind: 'none' };
}

/** Single-line form, for CSV export and other flat contexts. */
export function deploymentTargetLabel(asset: Asset): string {
    const target = deploymentTarget(asset);
    if (target.kind === 'none') return 'Unassigned';
    return target.kind === 'location' ? `Location: ${target.name}` : target.name;
}
